import React, { useEffect, useState } from "react";
import client from "@/lib/api";

/**
 * Storefront-wide launch countdown. Polls /launch-countdown and shows a
 * sticky bar under the header until 12h after the launch instant. Also
 * embeds a compact "Notify me" waitlist capture so thousands of pre-launch
 * visits convert into a warm mailing list.
 */
export default function LaunchCountdownBanner() {
  const [cfg, setCfg] = useState(null);
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem("moto_launch_dismissed") === "1"; } catch { return false; }
  });
  const [, setTick] = useState(0);
  const [email, setEmail] = useState("");
  const [waitBusy, setWaitBusy] = useState(false);
  const [waitState, setWaitState] = useState(""); // "" | "ok" | "already" | "err"

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      client.get("/launch-countdown")
        .then((r) => { if (!cancelled) setCfg(r.data); })
        .catch(() => {});
    };
    load();
    const poll = setInterval(load, 600000);
    const heartbeat = setInterval(() => setTick((t) => t + 1), 1000);
    return () => { cancelled = true; clearInterval(poll); clearInterval(heartbeat); };
  }, []);

  if (!cfg?.enabled || !cfg?.launch_at_iso || dismissed) return null;

  const targetMs = new Date(cfg.launch_at_iso).getTime();
  if (isNaN(targetMs)) return null;
  const now = Date.now();
  const diff = targetMs - now;
  if (diff < -1000 * 60 * 60 * 12) return null;

  const past = diff <= 0;
  const days = Math.max(0, Math.floor(diff / 86400000));
  const hours = Math.max(0, Math.floor((diff % 86400000) / 3600000));
  const mins = Math.max(0, Math.floor((diff % 3600000) / 60000));
  const secs = Math.max(0, Math.floor((diff % 60000) / 1000));
  const pad = (n) => String(n).padStart(2, "0");

  const theme = cfg.theme || "red";
  const cls = theme === "red"
    ? "bg-[#FF3B30] text-white"
    : theme === "ivory"
    ? "bg-[#FFF4EE] text-black border-b border-black/10"
    : "bg-black text-white";
  const inputCls = theme === "ivory"
    ? "bg-white text-black placeholder:text-black/40 border border-black/25"
    : "bg-white/10 text-white placeholder:text-white/60 border border-white/25 focus:bg-white/20";
  const btnCls = theme === "ivory"
    ? "bg-black text-white hover:bg-[#FF3B30]"
    : "bg-white text-black hover:bg-black hover:text-white";

  const dismiss = () => {
    try { sessionStorage.setItem("moto_launch_dismissed", "1"); } catch {}
    setDismissed(true);
  };

  const joinWaitlist = async (e) => {
    e.preventDefault();
    if (waitBusy) return;
    const em = (email || "").trim();
    if (!em || !/^\S+@\S+\.\S+$/.test(em)) {
      setWaitState("err");
      return;
    }
    setWaitBusy(true); setWaitState("");
    try {
      const r = await client.post("/waitlist", { email: em, source: "launch_countdown_banner" });
      setWaitState(r.data?.already_on_list ? "already" : "ok");
      setEmail("");
      setTimeout(() => setWaitState(""), 4500);
    } catch (err) {
      setWaitState("err");
    } finally {
      setWaitBusy(false);
    }
  };

  return (
    <div className={`px-4 md:px-12 py-2.5 flex items-center justify-between gap-3 flex-wrap ${cls}`} data-testid="launch-countdown-banner">
      <div className="flex items-center gap-3 flex-wrap min-w-0">
        <span className="label-eyebrow text-xs opacity-90">{cfg.title || "Launching soon"}</span>
        {past ? (
          <span className="text-sm">We're live — go grab something 🚀</span>
        ) : (
          <span className="font-mono tabular-nums text-sm md:text-base" aria-live="polite">
            {days > 0 ? `${pad(days)}d · ` : ""}{pad(hours)}h · {pad(mins)}m · {pad(secs)}s
          </span>
        )}
        {cfg.subtitle && <span className="text-xs opacity-80 hidden md:inline">{cfg.subtitle}</span>}
      </div>

      {/* Inline waitlist capture — hidden after launch */}
      {!past && (
        <form
          onSubmit={joinWaitlist}
          className="flex items-center gap-2 flex-wrap"
          data-testid="launch-waitlist-form"
        >
          {waitState === "ok" ? (
            <span className="text-xs" data-testid="launch-waitlist-ok">
              ✓ You're on the list — first in the door on launch day.
            </span>
          ) : waitState === "already" ? (
            <span className="text-xs" data-testid="launch-waitlist-already">
              ✓ Already on the list — you're good.
            </span>
          ) : (
            <>
              <input
                type="email"
                required
                placeholder="you@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                aria-label="Email address to join the launch waitlist"
                className={`text-xs px-3 py-1.5 min-w-[160px] focus:outline-none ${inputCls}`}
                data-testid="launch-waitlist-email"
              />
              <button
                type="submit"
                disabled={waitBusy}
                className={`label-eyebrow text-[10px] px-3 py-1.5 transition-colors disabled:opacity-50 ${btnCls}`}
                data-testid="launch-waitlist-submit"
              >
                {waitBusy ? "…" : "Notify me"}
              </button>
              {waitState === "err" && (
                <span className="text-[11px] opacity-90" data-testid="launch-waitlist-err">Enter a valid email.</span>
              )}
            </>
          )}
        </form>
      )}

      <div className="flex items-center gap-3">
        {cfg.cta_label && cfg.cta_href && (
          <a
            href={cfg.cta_href}
            className="label-eyebrow text-xs underline decoration-dotted underline-offset-4 hover:no-underline"
            data-testid="launch-countdown-cta"
          >
            {cfg.cta_label} →
          </a>
        )}
        <button
          onClick={dismiss}
          className="text-xs label-eyebrow opacity-70 hover:opacity-100"
          aria-label="Dismiss launch countdown"
          data-testid="launch-countdown-dismiss"
        >
          ✕
        </button>
      </div>
    </div>
  );
}
